// CTSK-322 — the Timeline Lane viz tab's source chip strip.
//
// Supersedes the CTSK-320 drawer-for-sources design, which shipped in
// 0.1.131 and did not survive first contact: putting the dataset toggles
// behind a closed-by-default drawer meant the operator could not see WHICH
// sources were feeding the chart without opening a panel, and a stale
// localStorage value (`timeline-lane:viz-sources` holding only keys that no
// longer exist) rendered an empty chart with no visible explanation.
//
// The strip fixes both by construction:
//   - it is ALWAYS VISIBLE above the iframe. There is no way to hide the
//     source list, so "which data is on screen" is never a hidden state.
//   - `loadStoredVizSources()` in the host auto-heals an empty intersection
//     back to ALL keys, and this component renders every key in
//     VIZ_SOURCE_OPTIONS regardless of what is stored — an unknown stored
//     key can never remove a chip.
//
// Layout:
//   SOURCES  [⬤ Events (242)] [⬤ Audit Timeline (218)] [○ Markers (3)] …  [+ Lab]   [🔒] [⤢] [⚙]
//
// Chip VISUALS live in globals.css (.viz-chip*), not inline, for a reason
// the first 0.1.132 build got wrong: an ON chip was painted
// background: var(--surface-raised) — the same colour as the strip behind
// it — so the pill boundary vanished and the row read as a chart legend.
// The operator's words were "Not toggles." Contrast against the strip,
// a dashed ghost for OFF, and :hover / :focus-visible (neither
// expressible inline) are what make a chip read as a control.
//
// Left group wraps to a second row rather than scrolling horizontally: a
// horizontally scrolled chip can be entirely off-screen, which is the same
// invisible-state failure the drawer had.
//
// Lab chips (CTSK-322) are dashed-bordered with an uppercase LAB badge and
// the dropped filename; they are session-only and never persisted. A chip
// whose source has an active DROPPED schema (CTSK-323) grows a mono
// `[schema]` suffix that posts back down to the viz to reveal the schema
// drop zone.

import { Maximize2, Minimize2, Settings } from 'lucide-react';
import { EditLockToggle } from '../EditLockToggle.js';

export interface VizChipOption {
  key: string;
  label: string;
}

/** A file dropped into the viz's Live Lab, mirrored up by postMessage. */
export interface LabChipSource {
  /** The dropped file's name — the lab chip's identity. */
  filename: string;
  /** The viz-side label (`LAB · <filename>`). */
  label: string;
  /** Post-validate row count from the viz. */
  count: number;
  /** True only when a dropped `.schema.yaml` is overriding this source. */
  hasSchemaOverride: boolean;
}

export interface TimelineVizChipStripProps {
  /** Canonical option list (stable `?sources=` order). */
  options: readonly VizChipOption[];
  /** Every known key — the auto-heal domain. Drives the "Show all"
   *  recovery chip that appears when the operator has turned everything
   *  off (the one state where the chart is blank by choice). */
  allKeys: readonly string[];
  onShowAll: () => void;
  /** Currently enabled source keys. */
  sources: string[];
  onToggle: (key: string) => void;
  /** Post-validate row counts, keyed by source key (viz `timeline-lab:counts`). */
  counts: Record<string, number | undefined>;
  /** Keys of REGISTERED sources with an active dropped-schema override. */
  schemaOverrides: Record<string, string | undefined>;
  labSources: LabChipSource[];
  onLabRemove: (filename: string) => void;
  /** `+ Lab` chip — asks the viz to reveal its data drop zone. */
  onOpenLab: () => void;
  /** `[schema]` badge — asks the viz to reveal its schema drop zone. */
  onOpenSchemaZone: () => void;
  locked: boolean;
  onToggleLock: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  drawerOpen: boolean;
  onToggleDrawer: () => void;
}

/** Per-source dot colors, mirroring the viz's own category palette so the
 *  chip and the points it controls read as the same thing:
 *  deep-time → astro violet, audit → tech green, markers → marker ink,
 *  future → transition amber, sessions → plan blue. */
const SOURCE_DOT: Record<string, string> = {
  events: '#7f6ee0',
  'audit-timeline-events': '#4fbf9f',
  markers: '#243047',
  'future-events': '#d97706',
  'sessions-timeline': '#2563eb',
};
const LAB_DOT = '#8a96a8';

// Only the bits CSS cannot key off state live here; everything visual is
// in the .viz-chip* rules.

function dot(color: string, filled: boolean) {
  return (
    <span
      aria-hidden
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flex: '0 0 auto',
        background: filled ? color : 'transparent',
        border: `1.5px solid ${color}`,
        opacity: filled ? 1 : 0.7,
      }}
    />
  );
}

export function TimelineVizChipStrip({
  options,
  allKeys,
  onShowAll,
  sources,
  onToggle,
  counts,
  schemaOverrides,
  labSources,
  onLabRemove,
  onOpenLab,
  onOpenSchemaZone,
  locked,
  onToggleLock,
  isFullscreen,
  onToggleFullscreen,
  drawerOpen,
  onToggleDrawer,
}: TimelineVizChipStripProps) {
  // The `[schema]` badge is a REAL button and therefore cannot live inside
  // the chip's toggle button — nested interactive content is invalid HTML
  // and browsers are free to restructure the subtree (React flags it as
  // validateDOMNesting). Every chip is therefore a <span> shell holding two
  // sibling buttons: the toggle, and (when present) the badge.
  const schemaSuffix = (filename: string | undefined, testId?: string) =>
    filename ? (
      <button
        type="button"
        data-testid={testId}
        onClick={onOpenSchemaZone}
        title={`Using dropped schema: ${filename}`}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          margin: 0,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: 'var(--text-muted)',
          cursor: 'pointer',
        }}
      >
        [schema]
      </button>
    ) : null;

  return (
    <div
      data-testid="viz-chip-strip"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        padding: '6px 8px',
        boxSizing: 'border-box',
        borderBottom: '1px solid var(--border-subtle)',
        // The drawer spans the container's full height (top: 0); the strip
        // stacks ABOVE it so ⚙ stays clickable while the drawer is open.
        position: 'relative',
        zIndex: 3,
        background: 'var(--surface-raised)',
      }}
    >
      {/* Sources — wraps to a second row rather than scrolling. */}
      <div
        role="group"
        aria-label="Chart data sources"
        style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}
      >
        <span className="viz-chip-strip-label" aria-hidden>Sources</span>
        {options.map((opt) => {
          const on = sources.includes(opt.key);
          const count = counts[opt.key];
          const overrideFile = schemaOverrides[opt.key];
          return (
            <span
              key={opt.key}
              data-testid={`viz-chip-${opt.key}`}
              className={`viz-chip${on ? '' : ' viz-chip--off'}`}
            >
              <button
                type="button"
                className="viz-chip-toggle"
                data-testid={`viz-chip-toggle-${opt.key}`}
                aria-pressed={on}
                onClick={() => onToggle(opt.key)}
                title={on ? `Hide ${opt.label} from the chart` : `Show ${opt.label} on the chart`}
              >
                {dot(SOURCE_DOT[opt.key] ?? LAB_DOT, on)}
                {opt.label}
                {count !== undefined && <span className="viz-chip-count">({count})</span>}
              </button>
              {schemaSuffix(overrideFile, `viz-chip-schema-${opt.key}`)}
            </span>
          );
        })}

        {/* Lab chips — dashed, session-only, removable on hover/focus. */}
        {labSources.map((lab) => (
          <span
            key={lab.filename}
            data-testid={`viz-lab-chip-${lab.filename}`}
            className="viz-chip viz-chip--lab"
          >
            {dot(LAB_DOT, true)}
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                padding: '0 4px',
                borderRadius: 3,
                background: 'var(--text-primary)',
                color: 'var(--surface-raised)',
              }}
            >
              lab
            </span>
            {lab.filename}
            <span className="viz-chip-count">({lab.count})</span>
            {lab.hasSchemaOverride && schemaSuffix(`schema override on ${lab.filename}`, `viz-lab-chip-schema-${lab.filename}`)}
            <button
              type="button"
              onClick={() => onLabRemove(lab.filename)}
              aria-label={`Remove ${lab.filename}`}
              title={`Remove ${lab.filename} from the chart`}
              style={{
                border: 'none',
                background: 'none',
                padding: '0 0 0 2px',
                color: 'var(--text-muted)',
                fontSize: 12,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          </span>
        ))}

        {/* + Lab — the way INTO the viz's Live Lab from the chrome. */}
        <button
          type="button"
          className="viz-chip viz-chip--add"
          data-testid="viz-chip-open-lab"
          onClick={onOpenLab}
          title="Open the viz's Live Lab and drop a local YAML file (never uploaded)"
        >
          + Lab
        </button>

        {/* Everything off is a legal (if blank) state — but it is also the
            shape the CTSK-320 stale-localStorage bug produced, so give the
            operator a one-click way back to all {allKeys.length}. */}
        {sources.length === 0 && (
          <button
            type="button"
            className="viz-chip viz-chip--add"
            data-testid="viz-chip-show-all"
            onClick={onShowAll}
            title={`Re-select all ${allKeys.length} data sources`}
            style={{ borderStyle: 'solid', borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)', fontWeight: 600 }}
          >
            Show all {allKeys.length}
          </button>
        )}
      </div>

      {/* Right rail — lock · expand · ⚙. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
        <EditLockToggle locked={locked} onToggle={onToggleLock} />
        <button
          type="button"
          onClick={onToggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand'}
          title={
            isFullscreen
              ? 'Exit fullscreen (ESC)'
              : 'Expand the timeline visualization to the full viewport (ESC exits)'
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 4,
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          {isFullscreen ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
        </button>
        <button
          type="button"
          onClick={onToggleDrawer}
          aria-label="Timeline settings"
          aria-pressed={drawerOpen}
          title={drawerOpen ? 'Close the chrome preferences drawer' : 'Open the chrome preferences drawer'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            fontSize: 12,
            border: '1px solid var(--border-strong)',
            borderRadius: 4,
            background: drawerOpen ? 'var(--surface-sunken)' : 'var(--surface-raised)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          <Settings size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}
